import { useState, useEffect } from "react";
import "./Users.css";
import FormGuidelines from "../components/FormGuidelines";
import Pagination from "../components/Pagination";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import { getDeletedUsers, restoreUser, getUnverifiedUsers, verifyUser, addDayOff, deleteDayOff, addNote, updateNote, deleteNote, getEmployeeProfile } from "../api/api";
import {
  ADMIN_ROLES,
  ROLE_VALUES,
  formatRoleLabel,
  hasAnyRole,
  hasRole,
  normalizeRole,
} from "../utils/roles";
import { normalizeDepartment } from "../utils/departments";

// ─────────────────────────────────────────────
// POSITION HELPERS
// ─────────────────────────────────────────────
const POSITION_CANONICAL = {
  FRONTEND_DEVELOPER:       "Frontend Developer",
  BACKEND_DEVELOPER:        "Backend Developer",
  UI_UX_DESIGNER:           "UI/UX Designer",
  SEO:                      "SEO Specialist",
  SEO_SPECIALIST:           "SEO Specialist",
  CONTENT_CREATOR:          "Content Creator",
  SOCIAL_MEDIA_SPECIALIST:  "Social Media Specialist",
  MEDIA_PRODUCTION:         "Media Production",
};

const normalizeKey = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[/-]/g, "_")
    .toUpperCase();

const normalizePosition = (value) => {
  if (!value) return "";
  const key = normalizeKey(value);
  return POSITION_CANONICAL[key] || value;
};

const mapRoleToApi = (role) => {
  switch (String(role).toLowerCase().trim()) {
    case "founder":      return "Founder";
    case "manager":      return "Manager";
    case "team leader":
    case "team_leader":  return "Team Leader";
    default:             return "Employee";
  }
};

// ─────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────
export default function Users() {
  const { currentUser, logout, users, loadingUsers, fetchUsers, removeUser, updateUser } = useAuth();

  const [showModal,    setShowModal]    = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isFromActions, setIsFromActions] = useState(false);
  const [search,       setSearch]       = useState("");
  const [roleFilter,   setRoleFilter]   = useState("ALL");
  const [currentPage,  setCurrentPage]  = useState(1);
  const itemsPerPage = 10;

  const [showDeletedModal, setShowDeletedModal] = useState(false);
  const [deletedUsers,     setDeletedUsers]     = useState([]);
  const [loadingDeleted,   setLoadingDeleted]   = useState(false);

  const [showVerifyModal,   setShowVerifyModal]   = useState(false);
  const [unverifiedUsers,   setUnverifiedUsers]   = useState([]);
  const [loadingUnverified, setLoadingUnverified] = useState(false);

  const [showActionsModal, setShowActionsModal] = useState(false);
  const [showDayOffModal,   setShowDayOffModal]   = useState(false);
  const [showNotesModal,    setShowNotesModal]    = useState(false);
  const [selectedUserForActions, setSelectedUserForActions] = useState(null);
  const [dayOffs, setDayOffs] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newDayOff, setNewDayOff] = useState({ date: "", type: "" });
  const [newNote, setNewNote] = useState({ note: "" });
  const [editingNote, setEditingNote] = useState(null);

  const role         = normalizeRole(currentUser?.role ?? currentUser?.user?.role ?? "");
  const isFounder    = role === ROLE_VALUES.FOUNDER;
  const isTeamLeader = role === ROLE_VALUES.TEAM_LEADER;
  const isAdmin      = hasAnyRole(role, ADMIN_ROLES);

  useEffect(() => {
    if (currentUser && users.length === 0) fetchUsers();
  }, [currentUser]);

  // ─────────────────────────────────────────
  // REFRESH DATA WHEN MODAL OPENS
  // ─────────────────────────────────────────
  useEffect(() => {
    if (showActionsModal && selectedUserForActions?._id) {
      const refreshData = async () => {
        try {
          const response = await getEmployeeProfile(selectedUserForActions._id);
          const userData = response.data || response;

          // Use the correct field name from API response: daysOff
          const dayOffData = userData.daysOff || userData.dayOffs || userData.dayoffs || userData.leaves || userData.leave ||
                            userData.dayOffRequests || userData.leaveRequests || [];
          // Notes is stored as a single string according to API response
          const notesData = userData.notes || "";

          setDayOffs(Array.isArray(dayOffData) ? dayOffData : []);
          setNotes(notesData ? [notesData] : []);
        } catch (err) {
          console.error("Failed to refresh data:", err);
        }
      };

      refreshData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showActionsModal, selectedUserForActions]);

  // ─────────────────────────────────────────
  // FILTER
  // ─────────────────────────────────────────
  const filteredUsers = (Array.isArray(users) ? users : [])
    .filter((user) =>
      user.userName?.toLowerCase().includes(search.toLowerCase()) ||
      user.email?.toLowerCase().includes(search.toLowerCase())
    )
    .filter((user) =>
      roleFilter === "ALL" ? true : normalizeRole(user.role) === roleFilter
    );

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [search, roleFilter]);

  // ─────────────────────────────────────────
  // DELETE
  // ─────────────────────────────────────────
  const handleDeleteUser = async (id) => {
    if (!isAdmin) return;

    const targetUser = users.find((u) => String(u._id) === String(id));

    if (isTeamLeader && hasRole(targetUser, ROLE_VALUES.FOUNDER)) {
      toast.error("Team leader can't delete founder");
      return;
    }

    try {
      await removeUser(id);
      if (String(currentUser?._id) === String(id)) {
        logout();
        return;
      }
      toast.success("User deleted");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to delete");
    }
  };

  // ─────────────────────────────────────────
  // OPEN EDIT MODAL
  // ─────────────────────────────────────────
  const openEdit = (user) => {
    if (!isAdmin) return;

    if (isTeamLeader && hasRole(user, ROLE_VALUES.FOUNDER)) {
      toast.error("Team leader can't edit founder");
      return;
    }

    setSelectedUser({
      ...user,
      department:  normalizeDepartment(user.department),
      position:    normalizePosition(user.position),
      password:    "",
      phoneNumber: user.phoneNumber || "",
    });
    setShowModal(true);
  };

  const openEditFromActions = () => {
    if (!selectedUserForActions) return;
    setIsFromActions(true);
    openEdit(selectedUserForActions);
  };

  const closeEditModal = () => {
    setShowModal(false);
    if (isFromActions) {
      setShowActionsModal(true);
      setIsFromActions(false);
    }
  };

  // ─────────────────────────────────────────
  // SAVE EDIT
  // ─────────────────────────────────────────
  const saveUser = async () => {
    if (!selectedUser.userName || !selectedUser.email || !selectedUser.department) {
      toast.error("Fill all required fields");
      return;
    }

    if (isTeamLeader && hasRole(selectedUser, ROLE_VALUES.FOUNDER)) {
      toast.error("You can't assign founder role");
      return;
    }

    try {
      const apiData = {
        userName:    selectedUser.userName,
        email:       selectedUser.email,
        department:  normalizeDepartment(selectedUser.department),
        role:        mapRoleToApi(selectedUser.role),
        phoneNumber: selectedUser.phoneNumber || "",
      };

      if (selectedUser.password) {
        apiData.password = selectedUser.password;
      }

      if (hasRole(selectedUser, ROLE_VALUES.FOUNDER)) {
        apiData.department = "CEO";
        delete apiData.position;
      } else if (selectedUser.department === "COO") {
        apiData.department = "COO";
        apiData.position   = "Other";
      } else {
  apiData.position = selectedUser.position
    ? normalizePosition(selectedUser.position)
    : "";
}

     // بعد updateUser أضف fetchUsers()
await updateUser(selectedUser._id, apiData);
await fetchUsers(); // ✅ أضف السطر ده
toast.success("User updated");
closeEditModal();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to update");
    }
  };

  // ─────────────────────────────────────────
  // DELETED USERS
  // ─────────────────────────────────────────
  const openDeletedUsers = async () => {
    setShowDeletedModal(true);
    setLoadingDeleted(true);
    try {
      const data = await getDeletedUsers();
      const list = Array.isArray(data)
        ? data
        : data.users ?? data.deletedUsers ?? data.data ?? [];
      setDeletedUsers(list);
    } catch {
      toast.error("Failed to load deleted users");
    } finally {
      setLoadingDeleted(false);
    }
  };

  const handleRestoreUser = async (id) => {
    try {
      await restoreUser(id);
      toast.success("User restored");
      setDeletedUsers((prev) => prev.filter((u) => String(u._id) !== String(id)));
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to restore");
    }
  };

  // ─────────────────────────────────────────
  // UNVERIFIED USERS
  // ─────────────────────────────────────────
 const openVerifyModal = async () => {
  setShowVerifyModal(true);
  setLoadingUnverified(true);
  try {
    const data = await getUnverifiedUsers();
   const list = Array.isArray(data)
  ? data
  : data?.data?.data ?? data?.data ?? data?.users ?? [];
    setUnverifiedUsers(list);
  } catch (err) {
    console.error(err);
    toast.error("Failed to load unverified users");
  } finally {
    setLoadingUnverified(false);
  }
};

  const handleVerifyUser = async (id) => {
    try {
      await verifyUser(id);
      toast.success("User verified successfully");
      setUnverifiedUsers((prev) => prev.filter((u) => String(u._id) !== String(id)));
      fetchUsers();
    } catch (err) {
      toast.error(err?.message || "Failed to verify user");
    }
  };

  // ─────────────────────────────────────────
  // ACTIONS MODAL
  // ─────────────────────────────────────────
  const openActionsModal = async (user) => {
    setSelectedUserForActions(user);

    // Fetch fresh data from API to ensure we have the latest information
    try {
      const response = await getEmployeeProfile(user._id);
      console.log("Fresh user data from API:", response);

      const userData = response.data || response;

      // Try multiple possible field names for day offs - daysOff is the correct one based on API response
      const dayOffData = userData.daysOff || userData.dayOffs || userData.dayoffs || userData.leaves || userData.leave ||
                        userData.dayOffRequests || userData.leaveRequests || [];

      // Notes is stored as a single string according to API response
      const notesData = userData.notes || "";

      console.log("Day off data from API:", dayOffData);
      console.log("Notes data from API:", notesData);

      setDayOffs(Array.isArray(dayOffData) ? dayOffData : []);
      setNotes(notesData ? [notesData] : []);
    } catch (err) {
      console.error("Failed to fetch fresh user data:", err);

      // Fallback to local user data if API fails
      const dayOffData = user.daysOff || user.dayOffs || user.dayoffs || user.leaves || user.leave || [];
      const notesData = user.notes || "";

      setDayOffs(Array.isArray(dayOffData) ? dayOffData : []);
      setNotes(notesData ? [notesData] : []);
    }

    setShowActionsModal(true);
  };

  const openDayOffModal = async () => {
    // Fetch fresh data from API first
    if (selectedUserForActions?._id) {
      try {
        const response = await getEmployeeProfile(selectedUserForActions._id);
        console.log("Fresh user data for day offs:", response);

        const userData = response.data || response;
        const dayOffData = userData.daysOff || userData.dayOffs || userData.dayoffs || userData.leaves || userData.leave ||
                          userData.dayOffRequests || userData.leaveRequests || [];

        setDayOffs(Array.isArray(dayOffData) ? dayOffData : []);
      } catch (err) {
        console.error("Failed to fetch day offs:", err);
      }
    }

    // Then switch modals
    setShowDayOffModal(true);
    setShowActionsModal(false);
  };

  const openNotesModal = async () => {
    // Fetch fresh data from API first
    if (selectedUserForActions?._id) {
      try {
        const response = await getEmployeeProfile(selectedUserForActions._id);
        console.log("Fresh user data for notes:", response);

        const userData = response.data || response;
        // Notes is stored as a single string according to API response
        const notesData = userData.notes || "";

        setNotes(notesData ? [notesData] : []);
      } catch (err) {
        console.error("Failed to fetch notes:", err);
      }
    }

    // Then switch modals
    setShowNotesModal(true);
    setShowActionsModal(false);
  };

  const closeAllModals = () => {
    setShowActionsModal(false);
    setShowDayOffModal(false);
    setShowNotesModal(false);
    setSelectedUserForActions(null);
    setNewDayOff({ date: "", type: "" });
    setNewNote({ note: "" });
    setEditingNote(null);
  };

  const backToActionsModal = () => {
    setShowDayOffModal(false);
    setShowNotesModal(false);
    setShowActionsModal(true);
  };

  // ─────────────────────────────────────────
  // DAY OFF FUNCTIONS
  // ─────────────────────────────────────────
  const handleAddDayOff = async () => {
    if (!newDayOff.date || !newDayOff.type) {
      toast.error("Please fill all day off fields");
      return;
    }

    try {
      const result = await addDayOff(selectedUserForActions._id, newDayOff);
      console.log("Day off result:", result);
      toast.success("Day off added successfully");

      // Refresh the user data to get the updated day offs
      await fetchUsers();

      // Fetch fresh data for the current user
      if (selectedUserForActions?._id) {
        const response = await getEmployeeProfile(selectedUserForActions._id);
        const dayOffData = response.data?.daysOff || response.data?.dayOffs || response.data?.dayoffs || response.data?.leaves || response.data?.leave || [];
        setDayOffs(Array.isArray(dayOffData) ? dayOffData : []);
      }

      setNewDayOff({ date: "", type: "" });
    } catch (err) {
      console.error("Day off error:", err);
      toast.error(err?.response?.data?.message || "Failed to add day off");
    }
  };

  const handleDeleteDayOff = async (dayOffId) => {
    try {
      await deleteDayOff(selectedUserForActions._id, dayOffId);
      toast.success("Day off deleted successfully");

      // Refresh the user data to get the updated day offs
      await fetchUsers();

      // Fetch fresh data for the current user
      if (selectedUserForActions?._id) {
        const response = await getEmployeeProfile(selectedUserForActions._id);
        const dayOffData = response.data?.daysOff || response.data?.dayOffs || response.data?.dayoffs || response.data?.leaves || response.data?.leave || [];
        setDayOffs(Array.isArray(dayOffData) ? dayOffData : []);
      }
    } catch (err) {
      console.error("Delete day off error:", err);
      toast.error(err?.response?.data?.message || "Failed to delete day off");
    }
  };

  // ─────────────────────────────────────────
  // NOTES FUNCTIONS
  // ─────────────────────────────────────────
  const handleAddNote = async () => {
    if (!newNote.note || newNote.note.trim() === "") {
      toast.error("Please enter note content");
      return;
    }

    if (newNote.note.length > 512) {
      toast.error("Note must be shorter than or equal to 512 characters");
      return;
    }

    try {
      await addNote(selectedUserForActions._id, { note: newNote.note });
      toast.success("Note added successfully");

      // Refresh the user data to get the updated notes
      await fetchUsers();

      // Fetch fresh data for the current user
      if (selectedUserForActions?._id) {
        const response = await getEmployeeProfile(selectedUserForActions._id);
        const userData = response.data || response;
        // Notes is stored as a single string according to API response
        const notesData = userData.notes || "";
        setNotes(notesData ? [notesData] : []);
      }

      setNewNote({ note: "" });
    } catch (err) {
      console.error("Add note error:", err);
      toast.error(err?.response?.data?.message || "Failed to add note");
    }
  };

  const handleUpdateNote = async () => {
    if (!editingNote || !editingNote.note || editingNote.note.trim() === "") {
      toast.error("Please enter note content");
      return;
    }

    if (editingNote.note.length > 512) {
      toast.error("Note must be shorter than or equal to 512 characters");
      return;
    }

    try {
      await updateNote(selectedUserForActions._id, { note: editingNote.note });
      toast.success("Note updated successfully");

      // Refresh the user data to get the updated notes
      await fetchUsers();

      // Fetch fresh data for the current user
      if (selectedUserForActions?._id) {
        const response = await getEmployeeProfile(selectedUserForActions._id);
        const userData = response.data || response;
        const notesData = userData.notes || "";
        setNotes(notesData ? [notesData] : []);
      }

      setEditingNote(null);
      setShowNotesModal(true);
    } catch (err) {
      console.error("Update note error:", err);
      toast.error(err?.response?.data?.message || "Failed to update note");
    }
  };

  const handleDeleteNote = async () => {
    try {
      // Since notes is a single string, we need to send empty string to delete
      await deleteNote(selectedUserForActions._id, { note: "" });
      toast.success("Note deleted successfully");

      // Refresh the user data to get the updated notes
      await fetchUsers();

      // Fetch fresh data for the current user
      if (selectedUserForActions?._id) {
        const response = await getEmployeeProfile(selectedUserForActions._id);
        const userData = response.data || response;
        const notesData = userData.notes || "";
        setNotes(notesData ? [notesData] : []);
      }

      setShowNotesModal(true);
    } catch (err) {
      console.error("Delete note error:", err);
      toast.error(err?.response?.data?.message || "Failed to delete note");
    }
  };

  // ─────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────
  if (loadingUsers && users.length === 0) return <p>Loading...</p>;

  return (
    <div className="users-page">
      <div className="users-header">
        <h1 className="users-title">Users Management</h1>

        {isAdmin && (
          <div className="header-actions">
            <button className="verify-users-btn" onClick={openVerifyModal} title="View Unverified Users">
              🔍 Verify Users
              {unverifiedUsers.length > 0 && (
                <span className="badge-count">{unverifiedUsers.length}</span>
              )}
            </button>
            <button className="deleted-users-btn" onClick={openDeletedUsers} title="View Deleted Users">
              🗑️ Deleted Users
            </button>
          </div>
        )}
      </div>

      <div className="users-controls">
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="ALL">All Roles</option>
          <option value={ROLE_VALUES.EMPLOYEE}>{formatRoleLabel(ROLE_VALUES.EMPLOYEE)}</option>
          <option value={ROLE_VALUES.TEAM_LEADER}>{formatRoleLabel(ROLE_VALUES.TEAM_LEADER)}</option>
          <option value={ROLE_VALUES.LEADER}>{formatRoleLabel(ROLE_VALUES.LEADER)}</option>
          <option value={ROLE_VALUES.MANAGER}>{formatRoleLabel(ROLE_VALUES.MANAGER)}</option>
          <option value={ROLE_VALUES.FOUNDER}>{formatRoleLabel(ROLE_VALUES.FOUNDER)}</option>
        </select>
      </div>

      <div className="users-card">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Department</th>
              <th>Position</th>
              <th>Role</th>
              {isAdmin && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 7 : 6} style={{ textAlign: "center", padding: "20px" }}>
                  No users found
                </td>
              </tr>
            ) : (
              paginatedUsers.map((user) => (
                <tr key={user._id}>
                  <td>{user.userName}</td>
                  <td>{user.email}</td>
                  <td>{user.phoneNumber || "—"}</td>
                  <td>{user.department}</td>
                  <td>{user.position || "—"}</td>
                  <td>{formatRoleLabel(user.role)}</td>
                  {isAdmin && (
                    <td className="actions">
                      {!hasRole(user, ROLE_VALUES.FOUNDER) && (
                        <button onClick={() => openActionsModal(user)} className="actions-btn">Actions</button>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onChange={setCurrentPage}
      />

      {/* ══ EDIT MODAL ══ */}
      {showModal && selectedUser && (
        <div className="modal-overlay" style={{ zIndex: 1001 }}>
          <div className="modal-box">
            <h2>Edit User</h2>
            <FormGuidelines />

            <input
              placeholder="Name *"
              value={selectedUser.userName || ""}
              onChange={(e) => setSelectedUser({ ...selectedUser, userName: e.target.value })}
            />
            <input
              placeholder="Phone Number"
              value={selectedUser.phoneNumber || ""}
              onChange={(e) => setSelectedUser({ ...selectedUser, phoneNumber: e.target.value })}
            />
            <input
              type="email"
              placeholder="Email *"
              value={selectedUser.email || ""}
              onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
            />
            <input
              type="password"
              placeholder="Reset Password (optional)"
              value={selectedUser.password || ""}
              onChange={(e) => setSelectedUser({ ...selectedUser, password: e.target.value })}
            />

            <select
              value={selectedUser.department || ""}
              onChange={(e) =>
                setSelectedUser({ ...selectedUser, department: e.target.value, position: "" })
              }
            >
              <option value="">Select Department *</option>
              <option value="Software">Software</option>
              <option value="Marketing">Marketing</option>
              <option value="CEO">CEO</option>
              <option value="COO">COO</option>
              <option value="HR">HR</option>
              <option value="Other">Other</option>
            </select>

            {/* Position — مخفي لو Founder أو COO */}
            {!hasRole(selectedUser, ROLE_VALUES.FOUNDER) && selectedUser.department !== "COO" && (
              <select
                value={selectedUser.position || ""}
                onChange={(e) => setSelectedUser({ ...selectedUser, position: e.target.value })}
              >
                <option value="">Select Position</option>

                {selectedUser.department === "Software" && (<>
                  <option value="Frontend Developer">Frontend Developer</option>
                  <option value="Backend Developer">Backend Developer</option>
                  <option value="UI/UX Designer">UI/UX Designer</option>
                </>)}

                {selectedUser.department === "Marketing" && (<>
                  <option value="SEO Specialist">SEO Specialist</option>
                  <option value="Content Creator">Content Creator</option>
                  <option value="Social Media Specialist">Social Media Specialist</option>
                  <option value="Media Production">Media Production</option>
                  <option value="Moderator">Moderator</option>
                  <option value="Graphic Designer">Graphic Designer</option>
                  <option value="Art Director">Art Director</option>
                  <option value="Media Buyer">Media Buyer</option>
                  <option value="Ads Advertising">Ads Advertising</option>
                </>)}

                {selectedUser.department === "HR" && (
                  <option value="HR">HR</option>
                )}

                {selectedUser.department === "Other" && (<>
                  <option value="Legal Affairs">Legal Affairs</option>
                  <option value="Accountant">Accountant</option>
                  <option value="Secretary">Secretary</option>
                  <option value="Office Boy">Office Boy</option>
                </>)}
              </select>
            )}

            <select
              value={normalizeRole(selectedUser.role) || ROLE_VALUES.EMPLOYEE}
              onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value })}
            >
              <option value={ROLE_VALUES.EMPLOYEE}>{formatRoleLabel(ROLE_VALUES.EMPLOYEE)}</option>
              <option value={ROLE_VALUES.TEAM_LEADER}>{formatRoleLabel(ROLE_VALUES.TEAM_LEADER)}</option>
              <option value={ROLE_VALUES.LEADER}>{formatRoleLabel(ROLE_VALUES.LEADER)}</option>
              <option value={ROLE_VALUES.MANAGER}>{formatRoleLabel(ROLE_VALUES.MANAGER)}</option>
              {isFounder && (
                <option value={ROLE_VALUES.FOUNDER}>{formatRoleLabel(ROLE_VALUES.FOUNDER)}</option>
              )}
            </select>

            <div className="modal-actions">
              <button onClick={closeEditModal}>Cancel</button>
              <button onClick={saveUser}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ DELETED USERS MODAL ══ */}
      {showDeletedModal && (
        <div className="modal-overlay">
          <div className="modal-box modal-box--wide">
            <h2>Deleted Users</h2>

            {loadingDeleted ? (
              <p>Loading...</p>
            ) : deletedUsers.length === 0 ? (
              <p>No deleted users found.</p>
            ) : (
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Department</th>
                    <th>Role</th>
                    <th>Restore</th>
                  </tr>
                </thead>
                <tbody>
                  {deletedUsers.map((user) => (
                    <tr key={user._id}>
                      <td>{user.userName}</td>
                      <td>{user.email}</td>
                      <td>{user.department}</td>
                      <td>{formatRoleLabel(user.role)}</td>
                      <td>
                        <button className="restore-btn" onClick={() => handleRestoreUser(user._id)}>
                          Restore
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="modal-actions">
              <button onClick={() => setShowDeletedModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ VERIFY USERS MODAL ══ */}
      {showVerifyModal && (
        <div className="modal-overlay">
          <div className="modal-box modal-box--wide">
            <h2>🔍 Verify Users</h2>
            <p className="modal-subtitle">Users waiting for verification</p>

            {loadingUnverified ? (
              <p>Loading...</p>
            ) : unverifiedUsers.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">✅</span>
                <p>No unverified users found.</p>
                <p className="empty-hint">All users have been verified!</p>
              </div>
            ) : (
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Department</th>
                    <th>Role</th>
                    <th>Created</th>
                    <th>Verify</th>
                  </tr>
                </thead>
                <tbody>
                  {unverifiedUsers.map((user) => (
                    <tr key={user._id} className="unverified-row">
                      <td>{user.userName}</td>
                      <td>{user.email}</td>
                      <td>{user.phoneNumber || "—"}</td>
                      <td>{user.department}</td>
                      <td>{formatRoleLabel(user.role)}</td>
                      <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                      <td>
                        <button className="verify-btn" onClick={() => handleVerifyUser(user._id)}>
                          ✓ Verify
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="modal-actions">
              <button onClick={() => setShowVerifyModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ ACTIONS MODAL ══ */}
      {showActionsModal && selectedUserForActions && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h2>User Actions</h2>
            <p className="modal-subtitle">Manage {selectedUserForActions.userName}'s account</p>

            <div className="actions-grid">
              <button onClick={openEditFromActions} className="action-card edit-action">
                <span className="action-icon">✏️</span>
                <span>Edit User</span>
              </button>
              <button onClick={() => handleDeleteUser(selectedUserForActions._id)} className="action-card delete-action">
                <span className="action-icon">🗑️</span>
                <span>Delete User</span>
              </button>
              <button onClick={openDayOffModal} className="action-card dayoff-action">
                <span className="action-icon">📅</span>
                <span>Day Off</span>
              </button>
              <button onClick={openNotesModal} className="action-card notes-action">
                <span className="action-icon">📝</span>
                <span>Notes</span>
              </button>
            </div>

            <div className="modal-actions">
              <button onClick={closeAllModals}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ DAY OFF MODAL ══ */}
      {showDayOffModal && selectedUserForActions && (
        <div className="modal-overlay">
          <div className="modal-box modal-box--wide">
            <h2>📅 Day Off Management</h2>
            <p className="modal-subtitle">Manage {selectedUserForActions.userName}'s day offs</p>

            <div className="add-dayoff-form">
              <input
                type="date"
                placeholder="Date"
                value={newDayOff.date}
                onChange={(e) => setNewDayOff({ ...newDayOff, date: e.target.value })}
                className="date-input-white"
              />
              <select
                value={newDayOff.type}
                onChange={(e) => setNewDayOff({ ...newDayOff, type: e.target.value })}
                className="type-select"
              >
                <option value="">Select Type</option>
                <option value="Annual">Annual</option>
                <option value="Casual">Casual</option>
                <option value="Sick">Sick</option>
              </select>
              <button onClick={handleAddDayOff} className="add-btn">Add Day Off</button>
            </div>

            {dayOffs.length === 0 ? (
              <p className="empty-state">No day offs recorded</p>
            ) : (
              <>
                <p style={{color: '#94a3b8', fontSize: '12px', marginBottom: '10px'}}>
                  Total day offs: {dayOffs.length}
                </p>
                <table className="dayoff-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {dayOffs.length > 0 ? (
                    dayOffs.map((dayOff, index) => {
                      console.log("Day off item:", dayOff);
                      console.log("Day off keys:", Object.keys(dayOff));

                      // Try multiple possible field names for date and format it properly
                      let dateValue = dayOff.date || dayOff.dateOfLeave || dayOff.startDate ||
                                     dayOff.leaveDate || dayOff.requestDate ||
                                     (dayOff.createdAt && new Date(dayOff.createdAt).toLocaleDateString()) ||
                                     'N/A';

                      // Format ISO date to readable format
                      if (dateValue && dateValue.includes('T')) {
                        dateValue = new Date(dateValue).toLocaleDateString();
                      }

                      // Try multiple possible field names for type
                      const typeValue = dayOff.type || dayOff.leaveType || dayOff.category ||
                                       dayOff.reason || dayOff.description ||
                                       dayOff.leaveCategory || 'N/A';

                      return (
                        <tr key={dayOff._id || dayOff.id || dayOff.date || index}>
                          <td className="white-text">{dateValue}</td>
                          <td className="white-text">{typeValue}</td>
                          <td>
                            <button onClick={() => handleDeleteDayOff(dayOff._id || dayOff.id)} className="delete-btn">
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="3" className="empty-state">No day offs recorded</td>
                    </tr>
                  )}
                </tbody>
              </table>
              </>
            )}

            <div className="modal-actions">
              <button onClick={backToActionsModal}>Back</button>
              <button onClick={closeAllModals}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ NOTES MODAL ══ */}
      {showNotesModal && selectedUserForActions && (
        <div className="modal-overlay">
          <div className="modal-box modal-box--wide">
            <h2>📝 Notes Management</h2>
            <p className="modal-subtitle">Manage {selectedUserForActions.userName}'s notes</p>

            <div className="add-note-form">
              <textarea
                placeholder="Write a note..."
                value={newNote.note}
                onChange={(e) => setNewNote({ note: e.target.value })}
                rows="3"
                maxLength={512}
              />
              <div style={{fontSize: '12px', color: '#94a3b8', marginBottom: '10px'}}>
                {newNote.note.length}/512 characters
              </div>
              <button onClick={handleAddNote} className="add-btn">Add Note</button>
            </div>

            {editingNote && (
              <div className="edit-note-form">
                <textarea
                  placeholder="Edit note..."
                  value={editingNote.note}
                  onChange={(e) => setEditingNote({ ...editingNote, note: e.target.value })}
                  rows="3"
                  maxLength={512}
                />
                <div style={{fontSize: '12px', color: '#94a3b8', marginBottom: '10px'}}>
                  {editingNote.note.length}/512 characters
                </div>
                <div className="edit-note-actions">
                  <button onClick={handleUpdateNote} className="save-btn">Update</button>
                  <button onClick={() => {
                    setEditingNote(null);
                    setShowNotesModal(true);
                  }} className="cancel-btn">Cancel</button>
                </div>
              </div>
            )}

            {notes.length === 0 ? (
              <p className="empty-state">No notes recorded</p>
            ) : (
              <button 
                onClick={() => setShowNotesModal(true)} 
                className="toggle-notes-btn"
              >
                📝 View Notes ({notes.length})
              </button>
            )}

            <div className="modal-actions">
              <button onClick={backToActionsModal}>Back</button>
              <button onClick={closeAllModals}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ NOTES VIEW MODAL ══ */}
      {showNotesModal && (
        <div className="modal-overlay">
          <div className="modal-box modal-box--small">
            <h2>📝 User Notes</h2>
            <p className="modal-subtitle">Notes for {selectedUserForActions?.userName}</p>

            {notes.length === 0 ? (
              <p className="empty-state">No notes recorded</p>
            ) : (
              <div className="notes-list">
                {notes.map((note, index) => {
                  const noteContent = typeof note === 'string' ? note : (note.note || note.content || 'No content');

                  return (
                    <div key={index} className="note-item">
                      <p className="note-content">{noteContent}</p>
                      <div className="note-actions">
                        <button onClick={() => {
                          setEditingNote({ note: noteContent });
                          setShowNotesModal(false);
                        }} className="edit-btn">Edit</button>
                        <button onClick={() => {
                          handleDeleteNote();
                          setShowNotesModal(false);
                        }} className="delete-btn">Delete</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="modal-actions">
              <button onClick={() => setShowNotesModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}